import React from "react";
import {
  PictureOutlined,
  PlayCircleOutlined,
  LinkOutlined,
  SendOutlined,
} from "@ant-design/icons";
import styles from "./styles.module.less";

export interface FacebookButton {
  type: "web_url" | "postback";
  title: string;
  url?: string;
  payload?: string;
}

export interface TemplatePayload {
  title?: string;
  subtitle?: string;
  text?: string;
  image_url?: string;
  imageUrl?: string;
  media_type?: string;
  media_url?: string;
  mediaUrl?: string;
  aspect_ratio?: string;
  buttons?: FacebookButton[];
  elements?: Array<{
    title?: string;
    subtitle?: string;
    image_url?: string;
    imageUrl?: string;
    buttons?: FacebookButton[];
  }>;
}

interface MessageTemplateContentProps {
  templateType: string;
  templatePayload: TemplatePayload;
}

export const MessageTemplateContent: React.FC<MessageTemplateContentProps> = ({
  templateType,
  templatePayload,
}) => {
  const payload = templatePayload;
  const upperType = templateType?.toUpperCase();
  const isGeneric = upperType === "GENERIC";
  const isButton = upperType === "BUTTON";
  const isMedia = upperType === "MEDIA";

  const handleButtonClick = (button: FacebookButton) => {
    if (button.type === "web_url" && button.url) {
      window.open(button.url, "_blank", "noopener,noreferrer");
    }
  };

  const renderButtons = (buttons: FacebookButton[] | undefined) => {
    if (!buttons || buttons.length === 0) return null;

    return (
      <div className={styles.templateMessageButtons}>
        {buttons.slice(0, 3).map((btn: FacebookButton, idx: number) => (
          <div
            key={idx}
            className={styles.templateMessageButton}
            onClick={() => handleButtonClick(btn)}
            style={{ cursor: btn.type === "web_url" ? "pointer" : "default" }}
          >
            {btn.type === "web_url" ? (
              <LinkOutlined style={{ fontSize: 12 }} />
            ) : (
              <SendOutlined style={{ fontSize: 12 }} />
            )}
            <span>{btn.title || "Button"}</span>
          </div>
        ))}
      </div>
    );
  };

  if (isGeneric) {
    const title = payload.title;
    const subtitle = payload.subtitle;
    const imageUrl = payload.image_url || payload.imageUrl;
    const buttons = payload.buttons;
    const elements = payload.elements;

    const firstElement =
      Array.isArray(elements) && elements.length > 0 ? elements[0] : null;
    const displayTitle = title || firstElement?.title;
    const displaySubtitle = subtitle || firstElement?.subtitle;
    const displayImageUrl =
      imageUrl || firstElement?.image_url || firstElement?.imageUrl;
    const displayButtons = buttons || firstElement?.buttons;

    return (
      <div className={styles.templateMessage}>
        {displayImageUrl ? (
          <img
            src={displayImageUrl}
            alt="template"
            className={styles.templateMessageImage}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className={styles.templateMessageImagePlaceholder}>
            <PictureOutlined style={{ fontSize: 20, color: "#fff" }} />
          </div>
        )}
        <div className={styles.templateMessageContent}>
          {displayTitle && (
            <div className={styles.templateMessageTitle}>{displayTitle}</div>
          )}
          {displaySubtitle && (
            <div className={styles.templateMessageSubtitle}>
              {displaySubtitle}
            </div>
          )}
        </div>
        {renderButtons(displayButtons)}
      </div>
    );
  }

  if (isButton) {
    const text = payload.text;
    const buttons = payload.buttons;

    return (
      <div className={styles.templateMessage}>
        {text && <div className={styles.templateMessageBubble}>{text}</div>}
        {buttons && buttons.length > 0 && (
          <div className={styles.templateMessageButtonList}>
            {buttons.slice(0, 3).map((btn: FacebookButton, idx: number) => (
              <div key={idx} className={styles.templateMessageButtonOutlined}>
                {btn.type === "web_url" ? (
                  <LinkOutlined style={{ fontSize: 12 }} />
                ) : (
                  <SendOutlined style={{ fontSize: 12 }} />
                )}
                <span>{btn.title || "Button"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (isMedia) {
    const mediaType = payload.media_type || "image";
    const mediaUrl = payload.media_url || payload.mediaUrl;
    const buttons = payload.buttons;
    const aspectRatio = payload.aspect_ratio || "horizontal";
    const height = aspectRatio === "square" ? 260 : 180;

    let mediaContent: React.ReactNode;
    
    if (mediaType === "video") {
      mediaContent = (
        <div className={styles.templateMessageVideo} style={{ height }}>
          {mediaUrl ? (
            <video
              src={mediaUrl}
              style={{
                width: "100%",
                height: "100%",
                minHeight: height + "px",
                objectFit: "cover",
              }}
              controls={false}
              preload="metadata"
            />
          ) : (
            <PlayCircleOutlined style={{ fontSize: 24, color: "#fff" }} />
          )}
          <div className={styles.templateMessagePlayOverlay}>
            <PlayCircleOutlined style={{ fontSize: 16, color: "#fff" }} />
          </div>
        </div>
      );
    } else if (mediaUrl) {
      mediaContent = (
        <img
          src={mediaUrl}
          alt="template"
          className={styles.templateMessageImage}
          style={{ height }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      );
    } else {
      mediaContent = (
        <div
          className={styles.templateMessageImagePlaceholder}
          style={{ height }}
        >
          <PictureOutlined style={{ fontSize: 20, color: "#fff" }} />
        </div>
      );
    }

    return (
      <div className={styles.templateMessage}>
        {mediaContent}
        {renderButtons(buttons)}
      </div>
    );
  }

  return null;
};

